
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { 
  Heart, 
  Reply,
  ChevronDown,
  ChevronUp,
  Plus
} from "lucide-react";
import ReplyForm from "./ReplyForm";
import { Comment } from "@/utils/commentsClient";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabaseExtended } from "@/integrations/supabase/extendedClient";

interface CommentItemProps {
  comment: Comment;
  onReply: (content: string) => Promise<void>;
  onLike: () => void;
  isLoading: boolean;
  isReply?: boolean;
  depth?: number;
}

const MAX_VISIBLE_DEPTH = 1; // First level replies are always visible

const CommentItem = ({ 
  comment, 
  onReply, 
  onLike, 
  isLoading, 
  isReply = false,
  depth = 0
}: CommentItemProps) => {
  const [replyingTo, setReplyingTo] = useState<number | string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [displayName, setDisplayName] = useState(comment.author);
  
  // Fetch the latest display name whenever the component mounts
  useEffect(() => {
    const fetchLatestDisplayName = async () => {
      if (!comment.author_address) return;
      
      try {
        const { data: profileData, error } = await supabaseExtended
          .from('profiles')
          .select('username')
          .eq('wallet_address', comment.author_address.toLowerCase())
          .maybeSingle();
          
        if (error) {
          console.error("Error fetching profile display name:", error);
          return;
        }
        
        if (profileData?.username) {
          setDisplayName(profileData.username);
        }
      } catch (error) {
        console.error("Error in fetchLatestDisplayName:", error);
      }
    };
    
    fetchLatestDisplayName();
  }, [comment.author_address]);
  
  // Determine if this reply and its children should be visible by default
  const hasHighlyLikedReply = comment.replies?.some(reply => 
    reply.likes > comment.likes
  );
  
  const shouldAutoExpand = depth <= MAX_VISIBLE_DEPTH || hasHighlyLikedReply;
  
  return (
    <div className={`flex gap-4 p-3 ${!isReply ? 'hover:bg-white/5' : ''} rounded-lg transition-colors`}>
      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center text-xs">
        {displayName[0]?.toUpperCase() || '?'}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Link 
            to={`/profile/${comment.author_address}`} 
            className="text-sm font-medium hover:underline hover:text-primary transition-colors"
          >
            {displayName}
          </Link>
          <span className="text-xs text-muted-foreground">{comment.timestamp}</span>
        </div>
        <p className="text-sm text-muted-foreground mb-2">{comment.content}</p>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            className={`text-xs gap-1 ${comment.liked_by_user ? 'text-primary' : ''}`}
            onClick={() => onLike()}
          >
            <Heart className={`h-3 w-3 ${comment.liked_by_user ? 'fill-primary' : ''}`} />
            {comment.likes}
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-xs gap-1"
            onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
          >
            <Reply className="h-3 w-3" />
            reply
          </Button>
        </div>
        
        {replyingTo === comment.id && (
          <ReplyForm
            onCancel={() => setReplyingTo(null)}
            onSubmit={(content) => onReply(content)}
            isLoading={isLoading}
          />
        )}
        
        {comment.replies && comment.replies.length > 0 && (
          <>
            {depth > MAX_VISIBLE_DEPTH && !hasHighlyLikedReply ? (
              // Use Collapsible for deeper nested replies
              <Collapsible className="mt-3 pl-2" open={isExpanded} onOpenChange={setIsExpanded}>
                <CollapsibleTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs w-full flex justify-start text-muted-foreground hover:text-foreground"
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-3 w-3 mr-1" />
                    ) : (
                      <Plus className="h-3 w-3 mr-1" />
                    )}
                    {isExpanded ? "Hide" : `Show ${comment.replies.length} ${comment.replies.length === 1 ? 'reply' : 'replies'}`}
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="pl-2 border-l border-border/40 space-y-3 mt-2">
                  {comment.replies.map(reply => (
                    <CommentItem 
                      key={reply.id} 
                      comment={reply} 
                      onReply={onReply}
                      onLike={onLike}
                      isLoading={isLoading}
                      isReply={true}
                      depth={depth + 1}
                    />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            ) : (
              // Show replies directly for first level or highly liked replies
              <div className="mt-3 pl-2 border-l border-border/40 space-y-3">
                {comment.replies.map(reply => (
                  <CommentItem 
                    key={reply.id} 
                    comment={reply} 
                    onReply={onReply}
                    onLike={onLike}
                    isLoading={isLoading}
                    isReply={true}
                    depth={depth + 1}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CommentItem;
